import React, { useState, useRef, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { FONT, kb } from '../components/apple2703d/ui';
import { collectEntries, parseReturn } from '../utils/apple2703d';
import Apple2703dReport, { CAVEAT } from '../components/apple2703d/Report';

// ─── Main component ───────────────────────────────────────────────────────────

function Apple2703dParser() {
  const [phase, setPhase] = useState('upload');
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState('');
  const [model, setModel] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const zipInput = useRef(null);
  const folderInput = useRef(null);

  useEffect(() => {
    if (folderInput.current) folderInput.current.setAttribute('webkitdirectory', '');
  }, [phase]);

  function addFiles(list) {
    const add = Array.from(list || []).filter(f => !/(^|\/)\.DS_Store$/.test(f.webkitRelativePath || f.name));
    if (!add.length) return;
    setFiles(prev => [...prev, ...add]);
    setError('');
  }

  async function handleParse() {
    if (!files.length) return;
    trackToolAction('/apple2703d', 'Apple Subpoena & 2703(d) Parser');
    setPhase('parsing');
    setError('');
    try {
      const entries = await collectEntries(files, setProgress);
      const parsed = await parseReturn(entries, setProgress);
      if (!parsed.datasets.length && !parsed.mail.length) {
        setError('No Apple data sheets were found. Upload the ZIP that the Apple Legal Process Download tool made (and, optionally, Apple\'s production ZIP).');
        setPhase('upload');
        return;
      }
      setModel(parsed);
      setPhase('report');
    } catch (err) {
      setError('Could not read those files: ' + (err?.message || String(err)));
      setPhase('upload');
    }
  }

  function reset() {
    setPhase('upload');
    setFiles([]);
    setModel(null);
    setError('');
  }

  // ── Upload view ──
  if (phase !== 'report') {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: FONT }}>
        <Navbar pageTitle="Apple Subpoena & 2703(d) Parser" />
        <div style={{ maxWidth: 660, margin: '0 auto', padding: '48px 24px 80px' }}>
          <div style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px', margin: 0 }}>Apple Subpoena & 2703(d) Parser</h2>
            <p style={{ color: '#64748b', marginTop: 6, fontSize: 15, lineHeight: 1.55 }}>
              Upload an Apple subpoena or 2703(d) return to browse subscriber details, iCloud activity, iMessage and FaceTime
              capability queries, iCloud Mail logs, devices, purchases and every IP address, per account and on one timeline.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20, padding: '13px 18px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, fontSize: 14, color: '#166534', lineHeight: 1.5 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span><strong>100% local &amp; private.</strong> Everything is read in your browser. Nothing is sent to any server.</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20, padding: '13px 18px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 14, color: '#92400e', lineHeight: 1.5 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>{CAVEAT}</span>
          </div>

          <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', padding: 28 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Upload both of these ZIP files:</div>
            <ol style={{ margin: '0 0 18px', paddingLeft: 20, color: '#334155', fontSize: 14, lineHeight: 1.7 }}>
              <li>
                <strong>Apple&rsquo;s production ZIP</strong>, the one Apple sent, named like <code style={{ fontSize: 12.5 }}>…_production_….zip</code>.
                It holds Apple&rsquo;s data workbook (devices, registrations, purchases, sign-ons, Game Center) and the Data Guide.
              </li>
              <li>
                <strong>The ZIP from the last step of the Apple Legal Process Download tool</strong>, whose name ends in{' '}
                <code style={{ fontSize: 12.5 }}>_flattened.zip</code>. It holds each account&rsquo;s decrypted data: account details, iCloud, iMessage, FaceTime and iCloud Mail logs.
              </li>
            </ol>
            <div
              onClick={() => zipInput.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              style={{
                border: `2px dashed ${dragOver ? '#1d4ed8' : '#bfdbfe'}`, borderRadius: 10, padding: '34px 24px', textAlign: 'center', cursor: 'pointer',
                background: dragOver ? '#eff6ff' : 'transparent', transition: 'border-color .2s, background .2s',
              }}
            >
              <input ref={zipInput} type="file" multiple accept=".zip,.xlsx,.csv,.txt,.eml,.pdf" style={{ display: 'none' }}
                onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
              <div style={{ width: 48, height: 48, borderRadius: 12, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', color: '#1d4ed8' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>Drop the ZIP files here, or click to choose</div>
              <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>You can add both ZIPs. Loose .xlsx / .csv files work too.</div>
            </div>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <input ref={folderInput} type="file" multiple style={{ display: 'none' }} onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
              <button onClick={() => folderInput.current?.click()}
                style={{ fontSize: 13, padding: '7px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontWeight: 600 }}>
                Or choose an unzipped folder…
              </button>
            </div>

            {files.length > 0 && (
              <div style={{ marginTop: 18, border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 180, overflowY: 'auto' }}>
                {Object.entries(files.reduce((acc, f) => {
                  const top = (f.webkitRelativePath || f.name).split('/')[0];
                  const k = f.webkitRelativePath ? `${top}/` : f.name;
                  acc[k] = acc[k] || { count: 0, size: 0 };
                  acc[k].count++; acc[k].size += f.size;
                  return acc;
                }, {})).map(([name, info]) => (
                  <div key={name} style={{ display: 'flex', gap: 10, padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#334155' }}>
                    <span style={{ fontWeight: 600, wordBreak: 'break-all' }}>{name}</span>
                    <span style={{ marginLeft: 'auto', color: '#94a3b8', whiteSpace: 'nowrap' }}>{info.count > 1 ? `${info.count} files · ` : ''}{kb(info.size)}</span>
                  </div>
                ))}
                <div style={{ padding: '6px 12px', textAlign: 'right' }}>
                  <button onClick={() => setFiles([])} style={{ fontSize: 12, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', textDecoration: 'underline' }}>Clear</button>
                </div>
              </div>
            )}

            {(() => {
              const names = files.map(f => (f.webkitRelativePath || f.name).split('/')[0]);
              const have = {
                production: names.some(n => /_production_/i.test(n)),
                flattened: names.some(n => /_flattened(\.zip)?$/i.test(n)),
              };
              const item = (ok, label) => (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, color: ok ? '#166534' : '#64748b' }}>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, background: ok ? '#dcfce7' : '#f1f5f9', border: `1px solid ${ok ? '#86efac' : '#cbd5e1'}` }}>{ok ? '✓' : ''}</span>
                  {label}{ok ? '' : ': not added yet'}
                </div>
              );
              return (
                <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {item(have.production, 'Apple production ZIP')}
                  {item(have.flattened, '_flattened ZIP from the Download tool')}
                  {files.length > 0 && !(have.production && have.flattened) && (
                    <div style={{ marginTop: 4, fontSize: 12.5, color: '#92400e', lineHeight: 1.5 }}>
                      {!have.flattened && !have.production
                        ? 'The file names don’t look like either ZIP. You can still parse them, but some data may be missing.'
                        : `Without the ${have.flattened ? 'production ZIP, devices, registrations, purchases and sign-ons' : '_flattened ZIP, account details and the iCloud, iMessage, FaceTime and Mail logs'} will be missing. You can still parse what you have.`}
                    </div>
                  )}
                </div>
              );
            })()}

            <button onClick={handleParse} disabled={!files.length || phase === 'parsing'}
              style={{
                display: 'block', width: '100%', marginTop: 18, padding: 13, background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 15, fontWeight: 700, cursor: !files.length || phase === 'parsing' ? 'not-allowed' : 'pointer', opacity: !files.length || phase === 'parsing' ? 0.55 : 1,
              }}>
              {phase === 'parsing' ? 'Parsing…' : 'Parse Return'}
            </button>
            {phase === 'parsing' && progress && <div style={{ marginTop: 10, fontSize: 12.5, color: '#64748b', textAlign: 'center' }}>{progress}</div>}
            {error && (
              <div style={{ marginTop: 16, padding: '12px 16px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, fontSize: 14 }}>{error}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return <Apple2703dReport model={model} onReset={reset} />;
}

export default Apple2703dParser;
